import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search, ExternalLink, Calendar, User, Newspaper, Tag } from 'lucide-react';
import UseAxiosSecure from '../hooks/UseAxiosSecure';

const BlogListAdmin = () => {
    const navigate = useNavigate();
    const axiosSecure = UseAxiosSecure();
    const [searchTerm, setSearchTerm] = useState('');

    // Fetch Published Blogs
    const { data: blogs = [], isLoading, isError } = useQuery({
        queryKey: ['admin-blogs-list'],
        queryFn: async () => {
            const res = await axiosSecure.get('/blogs_list');
            return res.data?.data || [];
        }
    });

    // Search filter logic
    const filteredBlogs = blogs.filter(blog => 
        blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.slug.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-4 md:p-6 max-w-7xl mx-auto font-sans text-white">
            
            {/* Top Bar Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl shadow-lg">
                        <Newspaper size={24} className="text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-black tracking-wide">Blog Management</h1>
                        <p className="text-xs text-slate-400">View and manage published search-optimized articles</p>
                    </div>
                </div>

                <button
                    onClick={() => navigate('/admin/add-blog')}
                    className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white text-xs font-bold rounded-2xl shadow-lg shadow-purple-500/20 transition-all self-start md:self-auto"
                >
                    <Plus size={16} />
                    <span>Create New Post</span>
                </button>
            </div>

            {/* Search Filter Bar */}
            <div className="bg-[#1a103c]/60 border border-white/10 rounded-2xl p-4 mb-6 backdrop-blur-xl flex items-center gap-3">
                <Search size={18} className="text-slate-400 ml-1" />
                <input 
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by article title or slug..."
                    className="bg-transparent border-none outline-none text-white placeholder-slate-500 text-sm w-full"
                />
            </div>

            {/* Data Table */}
            <div className="bg-[#1a103c]/60 border border-white/10 rounded-2xl overflow-hidden backdrop-blur-xl shadow-2xl">
                {isLoading ? (
                    <div className="p-12 text-center text-slate-400">
                        <div className="w-8 h-8 border-4 border-pink-500/30 border-t-pink-500 rounded-full animate-spin mx-auto mb-3"></div>
                        <p className="text-xs">Loading blog posts...</p>
                    </div>
                ) : isError ? (
                    <div className="p-12 text-center text-red-400 text-sm">
                        Failed to fetch blog posts. Please refresh the page.
                    </div>
                ) : filteredBlogs.length === 0 ? (
                    <div className="p-12 text-center text-slate-400">
                        <p className="text-base font-bold text-white mb-1">No Blog Posts Found</p>
                        <p className="text-xs">Create your first blog post to boost search engine indexing.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-white/10 bg-white/5 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                                    <th className="py-4 px-6">Article</th>
                                    <th className="py-4 px-6">Keywords</th>
                                    <th className="py-4 px-6">Author & Date</th>
                                    <th className="py-4 px-6 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5 text-xs">
                                {filteredBlogs.map((blog) => (
                                    <tr key={blog._id} className="hover:bg-white/5 transition-colors">
                                        
                                        {/* Title & Thumbnail */}
                                        <td className="py-4 px-6">
                                            <div className="flex items-center gap-4">
                                                <img 
                                                    src={blog.thumbnail} 
                                                    alt={blog.title} 
                                                    className="w-14 h-10 object-cover rounded-xl border border-white/10 shrink-0"
                                                />
                                                <div>
                                                    <h3 className="font-bold text-white line-clamp-1">{blog.title}</h3>
                                                    <p className="text-[10px] text-pink-400 font-mono">/blog/{blog.slug}</p>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Keywords */}
                                        <td className="py-4 px-6">
                                            <div className="flex flex-wrap gap-1 max-w-xs">
                                                {blog.keywords && blog.keywords.length > 0 ? (
                                                    blog.keywords.slice(0, 3).map((kw, idx) => (
                                                        <span key={idx} className="text-[9px] bg-white/5 border border-white/10 px-2 py-0.5 rounded text-slate-300">
                                                            {kw}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="text-[10px] text-slate-600">None</span>
                                                )}
                                            </div>
                                        </td>

                                        {/* Author & Date */}
                                        <td className="py-4 px-6 text-slate-400">
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-1.5">
                                                    <User size={12} className="text-pink-500" />
                                                    <span>{blog.author || 'Getsoko Team'}</span>
                                                </div>
                                                <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                                                    <Calendar size={12} />
                                                    <span>{new Date(blog.createdAt).toLocaleDateString()}</span>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Action Link */}
                                        <td className="py-4 px-6 text-right">
                                            <a 
                                                href={`/blog/${blog.slug}`} 
                                                target="_blank" 
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-xs font-bold text-blue-400 hover:text-blue-300 transition-all"
                                            >
                                                <span>View Live</span>
                                                <ExternalLink size={12} />
                                            </a>
                                        </td>

                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BlogListAdmin;